import { useEffect, useState } from "react";
import type { FormData, Formstatus } from "../../Types/contact.types";
import "./ContactUs.css";
import { submitContactForm } from "../../Services/contactApi";

const ContactUs = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    subject: "",
    department: "general",
    message: "",
  });

  const [formStatus, setFormStatus] = useState<Formstatus>({
    submitted: false,
    error: false,
  });

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      [data-aos] {
        opacity: 0;
        transition-property: opacity, transform;
      }
      [data-aos].aos-animate {
        opacity: 1;
      }
      [data-aos="fade-up"] {
        transform: translateY(50px);
      }
      [data-aos="fade-up"].aos-animate {
        transform: translateY(0);
      }
      [data-aos="fade-down"] {
        transform: translateY(-50px);
      }
      [data-aos="fade-down"].aos-animate {
        transform: translateY(0);
      }
      [data-aos="fade-left"] {
        transform: translateX(50px);
      }
      [data-aos="fade-left"].aos-animate {
        transform: translateX(0);
      }
      [data-aos="fade-right"] {
        transform: translateX(-50px);
      }
      [data-aos="fade-right"].aos-animate {
        transform: translateX(0);
      }
    `;
    document.head.appendChild(style);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("aos-animate");
          }
        });
      },
      { threshold: 0.1 },
    );

    document.querySelectorAll("[data-aos]").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setFormStatus({ submitted: false, loading: true, error: false });

    try {
      const result = await submitContactForm(formData);

      if (result.success) {
        setFormStatus({ submitted: true, loading: false, error: false });

        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          department: "general",
          message: "",
        });
        setTimeout(() => {
          setFormStatus({ submitted: false, loading: false, error: false });
        }, 5000);
      } else {
        setFormStatus({
          submitted: false,
          loading: false,
          error: true,
          errorMessage: result.message,
        });
      }
    } catch (error) {
      setFormStatus({
        submitted: false,
        loading: false,
        error: true,
        errorMessage: "An unexpected error occurred. Please try again",
      });
    }
  };

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="hero-particles"></div>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 text-center">
              <div className="contact-hero-tag" data-aos="fade-down">
                GET IN TOUCH
              </div>
              <h1
                className="contact-hero-title"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                Let's Start a{" "}
                <span className="gradient-text">Conversation</span>
              </h1>
              <p
                className="contact-hero-subtitle"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                Have a project in mind? Need training for your team? Looking for
                solar solutions or real estate opportunities? We're here to help
                you transform your vision into reality.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-methods">
        <div className="container">
          <div className="row g-4">
            <div
              className="col-lg-4 col-md-6"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="contact-method-card">
                <div className="method-icon">
                  <span>📧</span>
                </div>
                <h3>Email Us</h3>
                <p>Send us an email anytime</p>
                <a
                  href="mailto:support@learnandinnovatespace.com"
                  className="method-link"
                >
                  support@learnandinnovatespace.com
                </a>
              </div>
            </div>

            <div
              className="col-lg-4 col-md-6"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="contact-method-card">
                <div className="method-icon">
                  <span>📞</span>
                </div>
                <h3>Call Us</h3>
                <p>Mon-Fri from 8am to 5pm</p>
                <a href="tel:+2349078801125" className="method-link">
                  +234 907 880 1125
                </a>
              </div>
            </div>

            <div
              className="col-lg-4 col-md-6"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="contact-method-card">
                <div className="method-icon">
                  <span>⏰</span>
                </div>
                <h3>Office Hours</h3>
                <p>Monday - Friday</p>
                <span className="method-link">8:00 AM - 5:00 PM</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {formStatus.loading && (
        <div className="alert alert-info" role="alert">
          <strong>Sending... </strong> Please wait.
        </div>
      )}

      {formStatus.submitted && (
        <div className="alert alert-success" role="alert">
          <strong>Thank you! </strong> Your message has been sent successfully.
          We'll get back to you soon
        </div>
      )}

      <section className="main-contact">
        <div className="container">
          <div className="row g-5">
            <div className="col-lg-7" data-aos="fade-right">
              <div className="contact-form-wrapper">
                <div className="form-header">
                  <h2>Send Us a Message</h2>
                  <p>
                    Fill out the form below and we'll get back to you within 24
                    hours
                  </p>
                </div>

                <div className="contact-form">
                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="name">Full Name</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          className="form-control"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter your name"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          className="form-control"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter your email"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          className="form-control"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Enter your number"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="service">Service Interest</label>
                        <select
                          id="service"
                          name="service"
                          className="form-control"
                          value={formData.department}
                          onChange={handleChange}
                        >
                          <option value="">Select a service</option>
                          <option value="general">General Inquiry</option>
                          <option value="training">
                            Training & Entrepreneurship
                          </option>
                          <option value="solar">Solar Energy</option>
                          <option value="healthcare">Healthcare</option>
                          <option value="real-estate">Real Estate</option>
                        </select>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="message">Your Message</label>
                        <textarea
                          id="message"
                          name="message"
                          className="form-control"
                          rows={6}
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your project or inquiry..."
                        ></textarea>
                      </div>
                    </div>

                    <div className="col-12">
                      <button
                        onClick={handleSubmit}
                        className="btn btn-primary btn-submit"
                        disabled={formStatus.loading}
                      >
                        {formStatus.loading ? (
                          <>
                            <span className="spinner"></span>
                            Sending...
                          </>
                        ) : (
                          <>Send Message</>
                        )}
                      </button>

                      {formStatus.submitted && (
                        <div className="alert alert-success">
                          ✓ Thank you! Your message has been sent successfully.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-5" data-aos="fade-left">
              <div className="contact-info-wrapper">
                <div className="info-card">
                  <h3>Office Hours</h3>
                  <div className="info-item">
                    <span className="info-icon">🕐</span>
                    <div>
                      <p className="info-label">Monday - Friday</p>
                      <p className="info-value">8:00 AM - 5:00 PM</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">🕐</span>
                    <div>
                      <p className="info-label">Saturday</p>
                      <p className="info-value">9:00 AM - 2:00 PM</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">🕐</span>
                    <div>
                      <p className="info-label">Sunday</p>
                      <p className="info-value">Closed</p>
                    </div>
                  </div>
                </div>

                <div className="info-card">
                  <h3>Quick Response</h3>
                  <p className="info-text">
                    We typically respond to all inquiries within 24 hours during
                    business days. For urgent matters, please call us directly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-cta" data-aos="fade-up">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Get Started?</h2>
            <p>
              Let's discuss how LICO can help you achieve your training, energy,
              or real estate goals.
            </p>
            <a href="tel:+2349078801125" className="btn btn-light">
              Call Us Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
